<template>
  <div :class="['search', { 'search--open': isSearchOpen }]">
    <div class="search__inner">
      <div class="search__wrapper">
        <input
          id="searchInput"
          ref="searchInput"
          class="search__input"
          type="text"
          v-model="searchTerm"
          @input="handleInput"
          @keyup.enter="handleInputEnter"
          placeholder="Search a product or activity"
          aria-label="Search for products or activities"
        />
        <div v-if="isLoader" class="search__loader" aria-live="polite">
          <div v-for="(dot, index) in dots" :key="index" class="search__loader-dot" aria-hidden="true"></div>
        </div>
        <button
          v-if="searchTerm.length > 0"
          @click="searchTerm = ''"
          class="search__clear"
          tabindex="0"
          aria-label="Clear search input"
        >
          Clear
        </button>
        <button class="search__close" @click="toggleSearch(false)" aria-label="Close search">
          <svg role="img" aria-hidden="true" focusable="false">
            <use href="#close"></use>
          </svg>
        </button>
      </div>
      <div class="search__results">
        <div class="suggestion__wrapper">
          <div v-if="searchSuggestionObject.value && searchSuggestionObject.value.length" class="suggestion__inner">
            <span class="suggestion__title">{{ searchSuggestionObject.title }}</span>
            <ul class="suggestion__list">
              <li v-for="suggestion in searchSuggestionObject.value" tabindex="-1">
                <a
                  :href="`/pages/search-results?klevu_q=${removeHtmlTags(suggestion)}`"
                  :data-content="removeHtmlTags(suggestion)"
                  class="suggestion__item klevu-track-click"
                  v-html="suggestion"
                ></a>
              </li>
            </ul>
          </div>
          <div v-if="collectionSuggestions && collectionSuggestions.length" class="suggestion__inner is--desktop">
            <span class="suggestion__title">Collection Suggestions</span>
            <ul class="suggestion__list">
              <li v-for="suggestion in collectionSuggestions" tabindex="-1">
                <a
                  :href="suggestion.url"
                  :data-name="suggestion.name"
                  :data-url="suggestion.url"
                  class="suggestion__item klevu-track-click"
                  v-html="suggestion.name"
                  @click="trackClSuggestionClick(suggestion)"
                ></a>
              </li>
            </ul>
          </div>
        </div>
        <div class="products__wrapper">
          <div v-if="productSuggestionObject && productSuggestionObject.value.length" class="products__inner">
            <span class="suggestion__title">{{ productSuggestionObject.title }}</span>
            <ul class="products__list">
              <li
                v-for="product in productSuggestionObject.value"
                :data-id="product.id"
                ku-product-block
                class="klevuProduct"
              >
                <ProductBlock :key="product.id" :product="product" :searchTerm="searchTerm" />
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div
    :class="['search__backdrop', { 'search__backdrop--show': isSearchOpen }]"
    @click.prevent="toggleSearch(false)"
  ></div>
</template>

<script setup>
  import { ref, reactive, onBeforeMount, onMounted, computed, nextTick } from 'vue';
  import ProductBlock from './product-block.vue';

  const searchInput = ref(null);
  const dots = ref([1, 2, 3]);
  const searchTerm = ref('');
  const searchResults = reactive({
    suggestionList: [],
    collectionList: [],
    productList: [],
  });
  const noSearchResults = reactive({
    suggestionList: [],
    productList: [],
  });
  let debounceTimeout;
  const isSearchOpen = ref(false);
  const isLoader = ref(false);
  const urlRedirectsData = ref(null);
  let windowWidth = reactive(window.innerWidth);

  onBeforeMount(() => {
    window.addEventListener('resize', () => {
      windowWidth = window.innerWidth;
    });

    window.toggleSearch = toggleSearch;

    // Setting search term on search landing page
    const params = new window.URLSearchParams(window.location.search);
    const term = params.get('klevu_q');
    if (term) {
      searchTerm.value = term;
      fetchSearchResults();
    }
  });

  onMounted(async () => {
    // Suggestion list and product list data
    await fetch('https://js.klevu.com/klevu-js-v1/klevu-js-api/klevu-169515972703616748.json')
      .then((resp) => resp.text())
      .then((data) => {
        const parsedData = JSON.parse(data);
        noSearchResults.suggestionList = parsedData?.klevu_webstorePopularTerms;
        noSearchResults.productList = parsedData?.klevu_popularProductsOfSite;
      });

    await fetch('https://js.klevu.com/klevu-js-v1/klevu-js-api/klevu-169515972703616748-maps.json')
      .then((resp) => resp.text())
      .then((data) => {
        const parsedData = JSON.parse(data);
        urlRedirectsData.value = parsedData.klevu_keywordUrlMap;
      });
  });

  const searchSuggestionObject = computed(() => {
    if (searchTerm.value.length > 0) {
      return {
        title: 'Search Suggestions',
        value: searchResults.suggestionList.map((obj) => obj.suggest),
      };
    } else {
      return {
        title: 'Trending Searches',
        value: noSearchResults.suggestionList,
      };
    }
  });

  const collectionSuggestions = computed(() => {
    return searchTerm.value.length > 0 ? searchResults.collectionList : null;
  });

  const productSuggestionObject = computed(() => {
    if (searchTerm.value.length > 0) {
      return {
        title: 'Product Matches',
        value: searchResults.productList,
      };
    } else {
      return {
        title: 'Trending Products',
        value: noSearchResults.productList.slice(0, windowWidth > 767 ? 4 : 2),
      };
    }
  });

  const payload = computed(() => {
    return {
      context: {
        apiKeys: ['klevu-169515972703616748'],
      },
      suggestions: [
        {
          id: 'autosuggestion',
          query: searchTerm.value,
          typeOfRequest: 'AUTO_SUGGESTIONS',
          limit: 5,
        },
      ],
      recordQueries: [
        {
          id: 'collectionList',
          typeOfRequest: 'SEARCH',
          settings: {
            query: {
              term: searchTerm.value,
            },
            typeOfRecords: ['KLEVU_CATEGORY'],
            fields: ['name', 'url', 'id'],
            limit: 4,
            searchPrefs: ['searchCompoundsAsAndQuery'],
            sort: 'RELEVANCE',
          },
        },
        {
          id: 'productList',
          typeOfRequest: 'SEARCH',
          settings: {
            query: {
              term: searchTerm.value,
            },
            typeOfRecords: ['KLEVU_PRODUCT'],
            limit: windowWidth > 767 ? 4 : 2,
            searchPrefs: ['searchCompoundsAsAndQuery'],
            sort: 'RELEVANCE',
            fallbackQueryId: 'productListFallback',
          },
        },
      ],
    };
  });

  const fetchSearchResults = async () => {
    const apiUrl = `https://uscs32v2.ksearchnet.com/cs/v2/search`;
    isLoader.value = true;

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        body: JSON.stringify(payload.value),
      });
      const data = await response.text();
      const parsedData = JSON.parse(data);
      searchResults['suggestionList'] = parsedData.suggestionResults?.[0].suggestions;
      parsedData.queryResults.forEach((queryResult) => {
        searchResults[queryResult.id] = queryResult.records;
      });
      isLoader.value = false;
      trackSearchSuggestion(parsedData.queryResults.filter((queryResult) => queryResult.id === 'productList'));
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const trackSearchSuggestion = (data) => {
    if (data && searchTerm.value.length > 0) {
      const meta = data[0]?.meta;
      const url = `https://stats.ksearchnet.com/analytics/n-search/search?klevu_apiKey=klevu-169515972703616748&klevu_term=${meta.searchedTerm}&klevu_totalResults=${meta.totalResultsFound}&klevu_typeOfQuery=${meta.typeOfSearch}`;
      fetch(url).then(() => {
        return true;
      });
    }
  };

  const trackClSuggestionClick = (suggestion) => {
    const url = `https://stats.ksearchnet.com/analytics/productTracking?klevu_apiKey=klevu-169515972703616748&klevu_keywords=${searchTerm.value}&klevu_type=clicked&klevu_productId=${suggestion.id}&klevu_productName=${suggestion.name}&klevu_productUrl=${suggestion.url}`;
    fetch(url).then(() => {
      return true;
    });
  };

  const handleInput = () => {
    clearTimeout(debounceTimeout);

    debounceTimeout = setTimeout(() => {
      fetchSearchResults(searchTerm.value);
    }, 300);
  };

  const handleInputEnter = () => {
    const searchValue = searchTerm.value.trim();
    if (searchValue.length === 0) {
      return;
    }

    const normalizedTerm = searchValue.toLowerCase();

    const keywordToUrlMap = new Map();
    for (const entry of urlRedirectsData.value) {
      entry.keywords.forEach((keyword) => {
        keywordToUrlMap.set(keyword.toLowerCase(), entry.url);
      });
    }

    const redirectUrl = keywordToUrlMap.get(normalizedTerm);

    if (redirectUrl) {
      window.location.href = redirectUrl;
    } else {
      window.location.href = `/pages/search-results?klevu_q=${encodeURIComponent(searchValue)}`;
    }
  };

  const toggleSearch = (_open) => {
    const op = typeof _open === 'boolean' ? _open : !isSearchOpen.value;
    isSearchOpen.value = op;
    let bodyStyle = document.body.style;
    const searchWrapper = document.querySelector('.js-search');

    if (op) {
      nextTick(() => {
        searchInput.value.focus();
      });
      bodyStyle.overflow = 'hidden';
      searchWrapper.classList.add('is--active');
    } else {
      bodyStyle.overflow = 'auto';
      searchWrapper.classList.remove('is--active');
    }
  };

  const removeHtmlTags = (input) => {
    const regex = /(<([^>]+)>)/gi;
    return input.replace(regex, '');
  };
</script>
